import { computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Project } from '../../project/project.model';
import { ProjectEventBus } from '@web-portal/domains/shared/event-bus';

export class ProjectStore {
  private readonly _projects = signal<Project[]>([]);
  readonly projects = this._projects.asReadonly();

  private readonly _selectedProject = signal<Project | null>(null);
  readonly selectedProject = this._selectedProject.asReadonly();

  private readonly _projectIfcFilesToUpload = signal<File[]>([]);
  readonly projectIfcFilesToUpload = this._projectIfcFilesToUpload.asReadonly();

  readonly projectName = computed(() => this._selectedProject()?.name ?? '');

  private readonly projectEventBus = inject(ProjectEventBus);

  constructor() {
    this.projectEventBus
      .on$<Project | Project[]>('[Project] updated')
      .pipe(takeUntilDestroyed())
      .subscribe(event => {
        this.setProjects(event);
      });

    this.projectEventBus
      .on$<Project>('[Project] selected')
      .pipe(takeUntilDestroyed())
      .subscribe(event => {
        this._selectedProject.set(event);
      });

    this.projectEventBus
      .on$<File[] | null>('[Project] file-uploaded')
      .pipe(takeUntilDestroyed())
      .subscribe(event => {
        this._projectIfcFilesToUpload.set(event ?? []);
      });
  }

  private setProjects(projects: Project | Project[]): void {
    if (!Array.isArray(projects)) {
      this._projects.update(oldState =>
        oldState.some(project => project.id === projects.id)
          ? oldState.map(project => (project.id === projects.id ? projects : project))
          : [...oldState, projects]
      );
      this.refreshSelectedProject(current => (current.id === projects.id ? projects : current));
      return;
    }

    this._projects.set(projects);
    this.refreshSelectedProject(
      current => projects.find(project => project.id === current.id) ?? null
    );
  }

  /**
   * Keeps `selectedProject` in sync with incoming `[Project] updated` events: refreshes it
   * with the latest data when the selected project is part of the update, and lets the
   * resolver clear it (return `null`) when it's no longer present. Does nothing if no
   * project is currently selected.
   */
  private refreshSelectedProject(resolve: (current: Project) => Project | null): void {
    const current = this._selectedProject();
    if (!current) {
      return;
    }
    this._selectedProject.set(resolve(current));
  }
}
